import { IAvailabilityDay } from '@forwardflow/types/onboarding'

export const DAY_SHORT: Record<string, string> = {
   Monday: 'Mon',
   Tuesday: 'Tue',
   Wednesday: 'Wed',
   Thursday: 'Thu',
   Friday: 'Fri',
   Saturday: 'Sat',
   Sunday: 'Sun',
}

/** "9 am" or "9:30 am" from "09:00" / "09:30". */
export const clock = (t: string) => {
   const [h, m] = (t || '').split(':').map(Number)
   if (Number.isNaN(h)) return t
   const suffix = h >= 12 ? 'pm' : 'am'
   const hour = h % 12 || 12
   return m ? `${hour}:${String(m).padStart(2, '0')} ${suffix}` : `${hour} ${suffix}`
}

/** "Mon to Sat, 9 am to 6 pm" from a weekly availability. */
export const hoursSentence = (days: IAvailabilityDay[] | undefined) => {
   const on = (days || []).filter((d) => d.enabled)
   if (!on.length) return 'No days picked'
   const first = on[0]
   const last = on[on.length - 1]
   const sameTimes = on.every(
      (d) => d.startTime === first.startTime && d.endTime === first.endTime
   )
   const span =
      on.length === 1
         ? DAY_SHORT[first.day]
         : on.length === 7
           ? 'Every day'
           : `${DAY_SHORT[first.day]} to ${DAY_SHORT[last.day]}`
   return sameTimes ? `${span}, ${clock(first.startTime)} to ${clock(first.endTime)}` : `${span}, varies by day`
}

export const browserTimezone = () => {
   try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
   } catch {
      return ''
   }
}

/** The ten national digits of a US number, or null. */
export const nationalDigits = (phone?: string | null): string | null => {
   const digits = (phone || '').replace(/\D/g, '')
   const national =
      digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
   return national.length === 10 ? national : null
}

/** The three-digit US area code of a phone number, if it has one. */
export const areaCodeOf = (phone?: string | null): string | null => {
   const national = nationalDigits(phone)
   return national ? national.slice(0, 3) : null
}

/** "(719) 555-0100" from any US number; other input is returned as typed. */
export const prettyPhone = (phone?: string | null): string => {
   const national = nationalDigits(phone)
   if (!national) return phone || ''
   return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`
}

/** True for anything that looks like a dialable US number. */
export const isUsPhone = (phone?: string | null) => Boolean(nationalDigits(phone))

/** "+17195550100" from any US number. */
export const e164 = (phone?: string | null): string => {
   const national = nationalDigits(phone)
   return national ? `+1${national}` : phone || ''
}

/** Readable "A, B and C". */
export const listSentence = (names: string[]) => {
   if (names.length <= 1) return names.join('')
   return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

export const plural = (n: number, one: string, many = `${one}s`) =>
   `${n} ${n === 1 ? one : many}`
